import React, { useEffect, useState } from "react";
import { getStudentsWithPreviousAccounts, linkAccounts, unlinkStudentAccount } from "../../../network/Endpoint";
import StudentAccountLinkHeader from "./StudentAccountLinkHeader";
import { messageToast } from "../../../components/Toast";
import Modal from "../../../components/Modal";
import Button from "../components/Button";
import iconExclamationCircle from "../../../assets/images/icon-exclamation-circle-filled.png";
import radioCheckOn from "../../../assets/images/radio_check_on.png";
import radioCheckOff from "../../../assets/images/radio_check_off.png";
import linkTypeNotSelected from "../../../assets/images/link_type_not_selected.png";
import linkTypeSelectedReady from "../../../assets/images/link_type_selected_ready.png";
import linkTypeAlreadySelected from "../../../assets/images/link_type_already_selected.png";
import linkTypeDisable from "../../../assets/images/link_type_disable.png";
import helpIcon from "../../../assets/images/classes/icon-help-line.png";

const StudentAccountLinkPage = ({
    mainClass,
    setMainClass,
    classes,
    setClasses,
    teacherName,
    schoolName,
    fetchGetStudents
}) => {
    const [studentsData, setStudentsData] = useState([]);
    const [expandedStudents, setExpandedStudents] = useState({});
    const [selectedAccounts, setSelectedAccounts] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [isLinking, setIsLinking] = useState(false);
    const [isUnlinking, setIsUnlinking] = useState(false);
    const [isUnlinkAccountModalOpen, setIsUnlinkAccountModalOpen] = useState(false);
    const [unlinkTargetStudent, setUnlinkTargetStudent] = useState(null);
    const [isLinkedToThisClassModalOpen, setIsLinkedToThisClassModalOpen] = useState(false);
    const [linkedToThisClassAccount, setLinkedToThisClassAccount] = useState(null);

    // 학생 데이터 가져오기
    const fetchStudentsWithPreviousAccounts = async (classId, currentSelectedAccounts = {}) => {
        if (!classId || isLoading) return;

        setIsLoading(true);
        try {
            const body = { classId };
            const res = await getStudentsWithPreviousAccounts(body);

            const statusCode = res.data.status.code;
            const statusMessage = res.data.status.message;

            if (statusCode === 'Success') {
                const students = res.data.data.students || [];
                setStudentsData(students);

                // 연동 예정(LINKABLE) 상태인 학생들은 자동으로 확장 (단, 이미 선택된 학생은 확장하지 않음)
                const autoExpanded = {};
                students.forEach(student => {
                    if (student.linkStatus === 'LINKABLE' && !currentSelectedAccounts[student.student.studentId]) {
                        autoExpanded[student.student.studentId] = true;
                    }
                });
                setExpandedStudents(autoExpanded);
            } else {
                alert(`ERROR: 학생 정보를 가져오는 중 오류가 발생했습니다.\n${statusMessage}`);
            }
        } catch (error) {
            alert('학생 정보를 가져오는 중 오류가 발생했습니다.\n아크수학으로 문의해 주세요.');
        } finally {
            setIsLoading(false);
        }
    };

    // mainClass 변경 시 데이터 가져오기
    useEffect(() => {
        if (mainClass && mainClass.classId) {
            fetchStudentsWithPreviousAccounts(mainClass.classId);
            setSelectedAccounts({});
        }
    }, [mainClass?.classId]);

    // 학생 확장/축소 토글
    const toggleExpand = (studentId) => {
        const willExpand = !expandedStudents[studentId];

        setExpandedStudents(prev => ({
            ...prev,
            [studentId]: willExpand
        }));

        // 확장 영역이 열릴 때 선택된 계정 초기화
        if (willExpand) {
            setSelectedAccounts(prev => {
                const newState = { ...prev };
                delete newState[studentId];
                return newState;
            });
        }
    };

    // 계정 선택 (이미 선택된 계정 클릭 시에도 선택 유지)
    const toggleAccountSelection = (studentId, account) => {
        setSelectedAccounts(prev => {
            const current = prev[studentId];
            // 같은 accountId를 다시 클릭하면 선택 유지
            if (current && current.accountId === account.accountId) {
                return prev;
            }
            // 새로운 계정 선택
            return {
                ...prev,
                [studentId]: {
                    accountId: account.accountId,
                    accountIdValue: account.accountIdValue
                }
            };
        });

        // 계정 선택 시 확장 영역 닫기
        setExpandedStudents(prev => ({
            ...prev,
            [studentId]: false
        }));
    };

    // 개별 연동하기
    const handleIndividualLink = async (studentId, selectedAccount) => {
        if (!selectedAccount || !selectedAccount.accountId) {
            messageToast({ message: "연동할 계정을 선택해주세요!", description: "" });
            return;
        }

        if (isLinking) return;

        setIsLinking(true);
        try {
            const body = {
                accountId: selectedAccount.accountId,
                studentId: studentId
            };
            const res = await linkAccounts(body);

            const statusCode = res.data.status.code;
            if (statusCode === 'Success') {
                messageToast({ message: "계정이 연동되었습니다.", description: "" });
                // 데이터 새로고침 (현재 선택 상태 유지)
                await fetchStudentsWithPreviousAccounts(mainClass.classId, selectedAccounts);
                if (fetchGetStudents) {
                    await fetchGetStudents(mainClass.classId);
                }
            } else {
                alert(`ERROR: 계정 연동 중 오류가 발생했습니다.\n${res.data.status.message}`);
            }
        } catch (error) {
            alert('계정 연동 중 오류가 발생했습니다.\n아크수학으로 문의해 주세요.');
        } finally {
            setIsLinking(false);
        }
    };

    // 일괄 연동하기
    const handleBulkLink = async () => {
        if (!mainClass || !mainClass.classId) {
            alert('클래스 정보가 없습니다.');
            return;
        }

        if (Object.keys(selectedAccounts).length === 0) {
            alert('연동할 학생을 선택해주세요.');
            return;
        }

        if (isLinking) return;

        setIsLinking(true);
        try {
            // linkStudents 배열 생성
            const linkStudents = Object.entries(selectedAccounts).map(([studentId, account]) => ({
                studentId: parseInt(studentId),
                accountId: account.accountId
            }));

            const body = {
                classId: mainClass.classId,
                linkStudents: linkStudents
            };

            const res = await linkAccounts(body);

            const statusCode = res.data.status.code;
            if (statusCode === 'Success') {
                const successCount = linkStudents.length;
                messageToast({
                    message: "아이디 연동이 완료되었습니다.",
                    description: `${successCount}명 연동 성공`
                });

                // 모달 닫기
                setIsLinkModalOpen(false);

                // 선택된 계정 초기화
                setSelectedAccounts({});

                // 데이터 새로고침
                await fetchStudentsWithPreviousAccounts(mainClass.classId);
                if (fetchGetStudents) {
                    await fetchGetStudents(mainClass.classId);
                }
            } else {
                alert(`ERROR: 계정 연동 중 오류가 발생했습니다.\n${res.data.status.message}`);
            }
        } catch (error) {
            alert('계정 연동 중 오류가 발생했습니다.\n아크수학으로 문의해 주세요.');
        } finally {
            setIsLinking(false);
        }
    };

    // 선택 취소 (로컬 상태만 변경)
    const handleCancelSelection = (studentId) => {
        setSelectedAccounts(prev => {
            const newState = { ...prev };
            delete newState[studentId];
            return newState;
        });
        // 확장 영역 열기
        setExpandedStudents(prev => ({
            ...prev,
            [studentId]: true
        }));
    };

    // 연동 해제 모달 열기
    const openUnlinkModal = (student) => {
        setUnlinkTargetStudent(student);
        setIsUnlinkAccountModalOpen(true);
    };

    // 연동 해제 (서버에 요청)
    const handleUnlinkAccount = async () => {
        if (!unlinkTargetStudent) return;
        if (isUnlinking) return;

        setIsUnlinking(true);
        try {
            const body = { studentId: unlinkTargetStudent.studentId };
            const res = await unlinkStudentAccount(body);

            const statusCode = res.data.status.code;
            if (statusCode === 'Success') {
                messageToast({ message: "아이디 연동이 해제되었습니다.", description: `${unlinkTargetStudent.number}번 / ${unlinkTargetStudent.name}` });
                setIsUnlinkAccountModalOpen(false);
                setUnlinkTargetStudent(null);
                // 데이터 새로고침 (현재 선택 상태 유지)
                await fetchStudentsWithPreviousAccounts(mainClass.classId, selectedAccounts);
                if (fetchGetStudents) {
                    await fetchGetStudents(mainClass.classId);
                }
            } else {
                alert(`ERROR: 연동 해제 중 오류가 발생했습니다.\n${res.data.status.message}`);
            }
        } catch (error) {
            alert('연동 해제 중 오류가 발생했습니다.\n아크수학으로 문의해 주세요.');
        } finally {
            setIsUnlinking(false);
        }
    };

    // 특정 계정이 다른 학생에 의해 선택되었는지 확인
    const isAccountSelectedByOther = (currentStudentId, accountId) => {
        return Object.entries(selectedAccounts).some(
            ([studentId, account]) =>
                parseInt(studentId) !== currentStudentId &&
                account?.accountId === accountId
        );
    };

    // 연동 상태에 따른 UI 렌더링
    const renderLinkStatus = (linkStatus, studentId, student) => {
        // LINKED: 이미 연동됨
        if (linkStatus === 'LINKED') {
            return (
                <div className="flex items-center justify-end gap-3">
                    <div className="flex items-center justify-center gap-2">
                        <img src={linkTypeAlreadySelected} alt="연동됨" className="w-5 h-5"/>
                        <span className="text-[#009C5B] font-semibold text-[14px] w-[52px]">연동됨</span>
                    </div>
                    <button
                        className="text-[#FC5955] text-[12px] font-[700] border border-[#FC5955] rounded-[200px] px-[10px] py-[2px]"
                        onClick={() => openUnlinkModal(student)}
                    >
                        연동 해제
                    </button>
                </div>
            );
        }

        // LINKABLE: 연동 가능한 상태
        if (linkStatus === 'LINKABLE') {
            // 선택된 계정이 있으면 "연동 예정"
            if (selectedAccounts[studentId]) {
                return (
                    <div className="flex items-center justify-center gap-2">
                        <img src={linkTypeSelectedReady} alt="연동 예정" className="w-5 h-5" />
                        <span className="text-[#355BDF] font-semibold text-[14px] w-[52px]">연동 예정</span>
                    </div>
                );
            }
            // 선택된 계정이 없으면 "미연동"
            return (
                <div className="flex items-center justify-center gap-2">
                    <img src={linkTypeNotSelected} alt="미연동" className="w-5 h-5" />
                    <span className="text-[#979797] font-semibold text-[14px] w-[52px]">미연동</span>
                </div>
            );
        }

        // NOT_LINKABLE: 연동 불가
        return (
            <div className="flex items-center justify-center gap-2">
                <img src={linkTypeDisable} alt="연동 불가" className="w-5 h-5" />
                <span className="text-[#FC5955] font-semibold text-[14px] w-[52px]">연동 불가</span>
            </div>
        );
    };

    return (
        <>
            <div className="mt-8 text-xl font-bold text-center text-zinc-600 max-md:max-w-full">
                선생님, 확인해주세요!
            </div>
            <div className="my-4 text-base font-light text-center">
                <aside>
                    이 페이지에서 작년에 사용한 학생의 아크수학 아이디를 올해 클래스에 연동할 수 있습니다.<br/>
                    AI아크수학 개인 계정 정보를 잊어버린 학생을 위해<br/>
                    선생님이 직접 연동을 도와주실 수 있는 보조 페이지입니다.
                </aside>
            </div>
            <div className="mt-3 flex justify-center">
                <button
                    className="bg-[#7A95FF] rounded-full text-[#FFF] text-[20px] font-[700] px-[16px] py-[8px]"
                    onClick={() => window.open('https://1promath.notion.site/2025-30db8cfa9a12804e8e03ef67fcfa226a?pvs=74', '_blank')}
                >
                    자세히 보기
                </button>
            </div>

            <StudentAccountLinkHeader
                mainClass={mainClass}
                setMainClass={setMainClass}
                classes={classes}
                setClasses={setClasses}
                teacherName={teacherName}
                schoolName={schoolName}
                selectedAccountsCount={Object.keys(selectedAccounts).length}
                onLinkClick={() => setIsLinkModalOpen(true)}
            />

            {/* 학생 목록 테이블 */}
            <div className="w-full max-w-[1044px] mx-auto mt-5 mb-[150px]  flex flex-col gap-3">
                {/* 테이블 헤더 */}
                <div className="bg-[#F3F7FF] rounded-[10px] border border-[#DCE8FF] shadow-custom">
                    <div className="flex items-center h-[50px] px-4 text-[18px] font-semibold text-[#9BA5C2]">
                        <div className="w-[160px] text-center">번호</div>
                        <div className="w-[200px] text-center">학생 이름</div>
                        <div className="flex-1 text-center">연동 아크수학 아이디</div>
                        <div className="w-[200px] text-center">
                            연동 여부
                            <div className="relative inline-block ml-1 group">
                                <img
                                    className="cursor-help inline-block"
                                    src={helpIcon}
                                    alt="Help Icon"
                                />
                                {/* 툴팁 */}
                                <div className="absolute z-[100] w-fit bottom-full right-0 mb-2 px-3 py-3 text-white bg-gray-800 rounded-md text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                    <div className="flex flex-col gap-2 text-left">
                                        <div className="flex items-center gap-2">
                                            <img src={linkTypeAlreadySelected} alt="연동됨" className="w-4 h-4 flex-shrink-0"/>
                                            <span className="text-[#5ED3A3] font-semibold whitespace-nowrap">연동됨</span>
                                            <span className="whitespace-nowrap">: 이미 연동이 완료되어있는 상태</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <img src={linkTypeNotSelected} alt="미연동" className="w-4 h-4 flex-shrink-0"/>
                                            <span className="text-[#979797] font-semibold whitespace-nowrap">미연동</span>
                                            <span className="whitespace-nowrap">: 해당하는 아크수학 아이디를 선택할 수 있는 상태</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <img src={linkTypeSelectedReady} alt="연동 예정" className="w-4 h-4 flex-shrink-0 mt-[2px]"/>
                                            <span className="text-[#7494FF] font-semibold whitespace-nowrap">연동 예정</span>
                                            <span>: 아크수학 아이디가 선택되어 상단의 연동하기 버튼으로 연동할 수 있는 상태</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <img src={linkTypeDisable} alt="연동 불가" className="w-4 h-4 flex-shrink-0 mt-[2px]"/>
                                            <span className="text-[#FC5955] font-semibold whitespace-nowrap">연동 불가</span>
                                            <span>: 작년에 해당 학생 이름으로 연동된 아크수학 아이디가 없는 학생</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 테이블 바디 */}
                <div className="flex flex-col gap-6">
                    {isLoading ? (
                        <div className="py-10 text-center text-gray-500">
                            로딩 중...
                        </div>
                    ) : studentsData && studentsData.length > 0 ? (
                        studentsData.map((item, index) => (
                            <div key={item.student.studentId}>
                                {/* 메인 행 */}
                                <div
                                    className="relative z-10 flex items-center h-[56px] px-4 text-[14px] font-[500] text-[#5e6169] bg-white border border-[#E9E9E9] rounded-[10px] shadow-custom">
                                <div className="w-[160px] h-full border-r flex items-center justify-center">{item.student.number}</div>
                                    <div className="w-[200px] h-full text-center border-r flex items-center justify-center">{item.student.name}</div>
                                    <div className="flex-1 h-full text-center flex items-center justify-center gap-3 border-r">
                                        {item.student.id || selectedAccounts[item.student.studentId]?.accountIdValue ? (
                                            <>
                                                <span>{item.student.id || selectedAccounts[item.student.studentId]?.accountIdValue}</span>
                                                {selectedAccounts[item.student.studentId] && (
                                                    <button
                                                        onClick={() => handleCancelSelection(item.student.studentId)}
                                                        className="flex justify-center items-center w-[84px] h-[29px] text-[#FC5955] text-[14px] font-[700] border-2 border-[#FC5955] rounded-[200px]"
                                                    >
                                                        선택 취소
                                                    </button>
                                                )}
                                            </>
                                        ) : item.linkStatus === 'NOT_LINKABLE' ? (
                                            <span>전년도에 해당하는 이름의 아이디 없음</span>
                                        ) : (
                                            <span>아래에서 알맞은 학생 아이디를 선택하세요.</span>
                                        )}
                                    </div>
                                    <div className="w-[200px] text-center">
                                        {renderLinkStatus(item.linkStatus, item.student.studentId, item.student)}
                                    </div>
                                </div>

                                {/* 확장 영역 - matchingPreviousAccounts */}
                                <div
                                    className={`relative z-0 bg-[#F1F1F1] overflow-hidden transition-all duration-300 ease-in-out border-x border-b border-[#DADADA] rounded-b-[20px] mt-[-12px] ${
                                        expandedStudents[item.student.studentId]
                                            ? 'max-h-[2000px] opacity-100'
                                            : 'max-h-0 opacity-0 border-t-0'
                                    }`}
                                    style={{boxShadow: '0 4px 7px 0 rgba(0, 0, 0, 0.10)'}}
                                >
                                    {item.matchingPreviousAccounts && item.matchingPreviousAccounts.length > 0 ? (
                                        <div className=" relative pt-12 pb-8 px-[27px]">
                                            <div className="flex items-center gap-[6px] mb-2">
                                                <img src={iconExclamationCircle} alt="info" className="w-6 h-6" />
                                                <span className="text-[14px] text-[#5e6169] leading-[19px]">현재 반의 다른 학생에게 이미 연동된 아이디는 선택할 수 없습니다. 연동을 해제하시려면 해당 학생의 '연동 해제' 버튼을 이용해주세요.</span>
                                            </div>
                                            <div className="">
                                                {/* 서브 테이블 헤더 */}
                                                <div className="flex items-center h-[56px] bg-[#DADADA] rounded-[10px] text-[#50555F] text-center font-[700]">
                                                    <div className="flex-1">{item.previousYear || 25}년도 클래스 정보 (반/번호/이름)</div>
                                                    <div className="w-[200px]">아크수학 아이디</div>
                                                    <div className="w-[100px]">선택</div>
                                                </div>

                                                {/* 서브 테이블 바디 */}
                                                {item.matchingPreviousAccounts.map((account, accIndex) => {
                                                    const isSelectedByOther = isAccountSelectedByOther(item.student.studentId, account.accountId);
                                                    const isLinkedToThisClass = account.isLinkedToThisClass;
                                                    const isSelected = selectedAccounts[item.student.studentId]?.accountId === account.accountId;
                                                    const classes = account.previousClasses && account.previousClasses.length > 0
                                                        ? account.previousClasses
                                                        : [{ previousClassName: '-', studentNumber: null, studentName: '-' }];

                                                    return (
                                                        <div
                                                            key={accIndex}
                                                            onClick={() => {
                                                                if (isLinkedToThisClass) {
                                                                    setLinkedToThisClassAccount(account);
                                                                    setIsLinkedToThisClassModalOpen(true);
                                                                } else if (!isSelectedByOther) {
                                                                    toggleAccountSelection(item.student.studentId, account);
                                                                }
                                                            }}
                                                            className={`flex items-stretch transition-all border-b border-[#B3B3B3] ${
                                                                isSelected
                                                                    ? ''
                                                                    : isSelectedByOther
                                                                    ? 'bg-gray-100 opacity-60'
                                                                    : 'cursor-pointer hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            <div className={`flex-1 flex flex-col justify-center text-[15px] ${isSelectedByOther ? 'text-gray-400' : 'text-[#5e6169]'} ${isLinkedToThisClass ? 'opacity-50' : ''}`}>
                                                                {classes.map((cls, idx) => (
                                                                    <div key={idx} className="flex items-center justify-center min-h-[56px] border-t border-[#E0E0E0] first:border-t-0 px-4 text-center">
                                                                        {cls.previousClassName} 클래스{cls.studentNumber != null ? ` | ${cls.studentNumber}번` : ''}{cls.studentName ? ` | ${cls.studentName}` : ''}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className={`w-[200px] flex items-center justify-center text-center text-[15px] py-2 ${isSelectedByOther ? 'text-gray-400' : 'text-[#5e6169]'} ${isLinkedToThisClass ? 'opacity-50' : ''}`}>
                                                                {account.accountIdValue}
                                                            </div>
                                                            <div className="w-[100px] flex items-center justify-center">
                                                                {!isSelectedByOther && (
                                                                    <img
                                                                        src={isSelected ? radioCheckOn : radioCheckOff}
                                                                        alt="radio"
                                                                        className="w-8 h-8 mx-auto"
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-[#F8F8F8] px-4 py-6 text-center text-gray-500">
                                            연동 가능한 작년도 계정이 없습니다.
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="py-10 text-center text-gray-500">
                            학생 데이터가 없습니다.
                        </div>
                    )}
                </div>
            </div>

            {/* 연동하기 확인 모달 */}
            {isLinkModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-[20px] shadow-xl w-[544px] px-2 pb-8 pt-5 flex flex-col justify-center">
                        {/* 모달 헤더 */}
                        <h2 className="text-[24px] font-bold text-[#50555F] mb-3 text-center">연동하기</h2>
                        <div className="w-[522px] h-[1px] bg-[#D3D3D3]"></div>

                        {/* 모달 바디 */}
                        <div className="mt-5 mb-3">
                            <p className="text-[24px] mb-8 text-center">
                                <span className="text-[#355BDF] font-semibold">{Object.keys(selectedAccounts).length}명의 학생을</span>
                                <span className="text-[#50555F]"> 연동 하시겠습니까?</span>
                            </p>

                            {/* 학생 목록 테이블 */}
                            <div className="w-[447px] max-h-[462px] mx-auto overflow-hidden">
                                {/* 테이블 헤더 */}
                                <div className="flex items-center h-[50px] bg-[#F3F7FF] text-center text-[18px] font-[700] text-[#9BA5C2] rounded-t-[20px]">
                                    <div className="w-[100px] ">번호</div>
                                    <div className="w-[144px]">이름</div>
                                    <div className="flex-1">연동할 아이디</div>
                                </div>

                                {/* 테이블 바디 */}
                                <div className="max-h-[412px] overflow-y-auto border-x">
                                    {studentsData
                                        .filter(item => selectedAccounts[item.student.studentId])
                                        .map((item, index) => (
                                            <div key={item.student.studentId} className="flex items-center h-[50px] border-b border-[#E9E9E9]  text-center text-[14px] text-[#50555F]">
                                                <div className="w-[100px] border-r border-r-[#E9E9E9] h-full flex justify-center items-center">{item.student.number}</div>
                                                <div className="w-[144px] border-r border-r-[#E9E9E9] h-full flex justify-center items-center">{item.student.name}</div>
                                                <div className="flex-1">
                                                    {selectedAccounts[item.student.studentId]?.accountIdValue}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>

                        {/* 모달 푸터 (버튼) */}
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setIsLinkModalOpen(false)}
                                disabled={isLinking}
                                className="w-[120px] h-[44px] bg-[#BFBFBF] rounded-[200px] text-[20px] font-[700] text-white hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleBulkLink}
                                disabled={isLinking}
                                className="w-[120px] h-[44px] bg-[#7494FF] rounded-[200px] text-[20px] font-[700] text-white hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLinking ? '연동 중...' : '연동하기'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 아이디 연동 해제 확인 모달 */}
            <Modal
                isOpen={isUnlinkAccountModalOpen}
                onClose={() => setIsUnlinkAccountModalOpen(false)}
            >
                <h2 className="text-[28px] font-[700] mb-3">아이디 연동 해제</h2>
                <div className="border-[1px] bg-[#D3D3D3] w-[416px] mx-[15px]"></div>
                <p className="mt-8 mb-8 text-2xl font-normal">
                    <span className="text-[#355bdf]">[{unlinkTargetStudent?.id}]</span><br/>
                    아이디 연동을 해제하시겠습니까?
                </p>
                <div className="flex justify-center gap-4">
                    <Button
                        label="취소"
                        className="bg-[#BFBFBF] text-black hover:bg-gray-400"
                        onClick={() => setIsUnlinkAccountModalOpen(false)}
                        disabled={isUnlinking}
                    />
                    <Button label="연동 해제" onClick={handleUnlinkAccount} disabled={isUnlinking}/>
                </div>
            </Modal>

            {/* 같은 클래스 내 중복 연동 불가 안내 모달 */}
            <Modal
                isOpen={isLinkedToThisClassModalOpen}
                onClose={() => setIsLinkedToThisClassModalOpen(false)}
            >
                <h2 className="text-[28px] font-[700] mb-3">아크수학 아이디 연동</h2>
                <div className="border-[1px] bg-[#D3D3D3] w-[416px] mx-[15px]"></div>
                <div className="mt-8 mb-8 text-xl font-normal text-center">
                    {(() => {
                        const linkedStudent = linkedToThisClassAccount
                            ? studentsData.find(s => s.student.id === linkedToThisClassAccount.accountIdValue)
                            : null;
                        return linkedStudent ? (
                            <p className="mb-3">
                                <span className="text-[#355bdf] font-semibold">[{linkedStudent.student.number}번 {linkedStudent.student.name}]</span>에 연동된 아이디 입니다.
                            </p>
                        ) : null;
                    })()}
                    <p>같은 클래스 내에서는 한 아이디를<br/>여러명에게 연동할 수 없습니다.</p>
                </div>
                <div className="flex justify-center">
                    <Button
                        label="확인"
                        onClick={() => setIsLinkedToThisClassModalOpen(false)}
                    />
                </div>
            </Modal>
        </>
    );
};

export default StudentAccountLinkPage;
